﻿<svelte:options
	customElement={{
		tag: "contact-form",
		shadow: "none",
	}}
/>

<script lang="ts">
	import { onMount, tick } from "svelte";
	import ky from "ky";
	import { create, enforce, test, group, skip } from "vest";
	import "vest/enforce/email";
	import type { ProblemDocument } from "http-problem-details";

	import { ValidatingStore, type ValidationProblemDetails } from "./validating-store";
	import { FormState } from "./utils";

	interface ContactRequest {
		fullname: string;
		company: string;
		email: string;
		phone: string;
		message: string;
	}

	const initialFormData: ContactRequest = {
		fullname: "",
		company: "",
		email: "",
		phone: "",
		message: "",
	};

	export let source: string;

	let state: FormState = FormState.Ready;
	let token: string = "";
	let problem: ProblemDocument | null = null;

	const store = new ValidatingStore(initialFormData, (data: ContactRequest) => {
		test("fullname", "Bitte geben Sie Ihren Namen an", () => {
			enforce(data.fullname).isNotBlank();
		});
		test("email", () => {
			enforce(data.email).message("Bitte geben Sie Ihre E-Mail-Adresse an").isNotBlank().message("Bitte geben Sie eine gültige E-Mail-Adresse an").isEmail();
		});
		test("message", "Bitte geben Sie eine Nachricht ein", () => {
			enforce(data.message).isNotBlank();
		});
	});

	onMount(() => {
		let tokenElement = document.getElementById("RequestVerificationToken");
		if (tokenElement == null || tokenElement.textContent == null) {
			throw new Error("Could not load XSRF token.");
		}
		token = JSON.parse(tokenElement.textContent);
	});

	async function submitAsync() {
		if (state !== FormState.Ready) {
			return;
		}
		if (!store.validate()) {
			return;
		}

		try {
			state = FormState.Loading;
			let response = await ky.post(`/api/contact`, {
				headers: { RequestVerificationToken: token },
				json: store.getData(),
				throwHttpErrors: false,
			});

			if (response.ok) {
				state = FormState.Success;
				store.clear();
			} else if (response.status === 400) {
				state = FormState.Ready;
				problem = await response.json<ValidationProblemDetails>();
				store.setExternalErrors(problem);
			} else {
				problem = await response.json<ProblemDocument>();
				state = FormState.Error;
			}
		} catch (e) {
			state = FormState.Error;
		}
	}
</script>

<div class="contact-form">
	<div class="field">
		<div class="control">
			<label class="label is-floating" for="fullname">Vor- und Nachname</label>
			<input id="fullname" class="input" class:is-danger={$store.fullname.hasErrors} type="text" autocomplete="name" placeholder="" bind:value={$store.fullname.value} on:blur={() => $store.fullname.touch()} />
		</div>
		{#if $store.fullname.hasErrors}
			<p class="help is-danger">{$store.fullname.errors.join(", ")}</p>
		{/if}
	</div>

	<div class="field">
		<div class="control">
			<label class="label is-floating" for="company">Firma</label>
			<input id="company" class="input" class:is-danger={$store.company.hasErrors} type="text" autocomplete="organization" placeholder="" bind:value={$store.company.value} on:blur={() => $store.company.touch()} />
		</div>
		{#if $store.company.hasErrors}
			<p class="help is-danger">{$store.company.errors.join(", ")}</p>
		{/if}
	</div>

	<div class="field">
		<div class="control">
			<label class="label is-floating" for="phone">Telefon</label>
			<input id="phone" class="input" class:is-danger={$store.phone.hasErrors} type="tel" autocomplete="tel" placeholder="" bind:value={$store.phone.value} on:blur={() => $store.phone.touch()} />
		</div>
		{#if $store.phone.hasErrors}
			<p class="help is-danger">{$store.phone.errors.join(", ")}</p>
		{/if}
	</div>

	<div class="field">
		<div class="control">
			<label class="label is-floating" for="email">E-Mail</label>
			<input id="email" class="input" class:is-danger={$store.email.hasErrors} type="email" autocomplete="email" placeholder="" bind:value={$store.email.value} on:blur={() => $store.email.touch()} />
		</div>
		{#if $store.email.hasErrors}
			<p class="help is-danger">{$store.email.errors.join(", ")}</p>
		{/if}
	</div>

	<div class="field">
		<div class="control">
			<label class="label is-floating" for="message">Nachricht</label>
			<textarea id="message" class="textarea" class:is-danger={$store.message.hasErrors} rows="6" placeholder="" bind:value={$store.message.value} on:blur={() => $store.message.touch()}></textarea>
		</div>
		{#if $store.message.hasErrors}
			<p class="help is-danger">{$store.message.errors.join(", ")}</p>
		{/if}
	</div>
	{#if state === FormState.Success}
		<div class="notification is-success">
			<strong>Vielen Dank für Ihre Nachricht</strong>
		</div>
	{:else if state === FormState.Error}
		<div class="notification is-danger">
			<p>Beim Senden Ihrer Nachricht ist ein Fehler aufgetreten</p>
			<p>Bitte kontaktieren Sie uns direkt unter <a href={`mailto:info@von-ag.ch?message=${encodeURIComponent($store.message.value)}`}>info@von-ag.ch</a></p>
			{#if problem}
				<div>
					<small>
						<em>{problem?.title}</em><br />
						{problem?.detail}
					</small>
				</div>
			{/if}
		</div>
	{:else}
		<div class="field is-grouped">
			<p class="control">
				<button class="button is-primary" class:is-loading={state === FormState.Loading} disabled={state !== FormState.Ready} on:click={() => submitAsync()}>Absenden</button>
			</p>
		</div>
	{/if}
</div>
